import {
    getAchievementLevelListApi,
    getAchievementListApi,
    getAchievementSettingApi
} from 'api/achievement';
import { objFirstLevelJSONParse } from 'utils/objectManipulate';

const INIT_ACHIEVEMENT = 'achievement/INIT_ACHIEVEMENT';
export const UPDATE_ACHIEVEMENT = 'achievement/UPDATE_ACHIEVEMENT';
export const SET_ACHIEVEMENT_INITIALIZED = 'achievement/SET_ACHIEVEMENT_INITIALIZED';
export const SET_CURRENT_ACHIEVEMENT_INFO = 'achievement/SET_CURRENT_ACHIEVEMENT_INFO';
export const SET_SELECTED_SECTION_ID = 'achievement/SET_SELECTED_SECTION_ID';
export const UPDATE_CURRENT_ACHIEVEMENT_INFO = 'achievement/UPDATE_CURRENT_ACHIEVEMENT_INFO';
export const keyVisionSectionId = 'keyVision';
export const customLinkSectionId = 'customLink';
export const descriptionSectionId = 'description';
export const levelDescriptionSectionId = 'levelDescription';
export const progressBarSectionId = 'progressBar';

export const FOUNDATION_CODE = {
    STYLES: 'Styles',
    ACHIEVEMENTS: 'Achievements'
};

const initialState = {
    // 成就中心列表
    achievementList: [],
    // 成就中心等級列表
    achievementLevelList: [],
    // 當前選擇成就中心資訊
    currentAchievementInfo: {},
    flag: {
        // 列表初始化狀態
        isInitialized: false,
        // 編輯選擇的 section id
        sectionId: null
    }
};

export default function reducer(state = initialState, { type, payload }) {
    switch (type) {
        case INIT_ACHIEVEMENT:
            return { ...payload };
        case UPDATE_ACHIEVEMENT:
            return { ...state, ...payload };
        case SET_ACHIEVEMENT_INITIALIZED:
            return { ...state, flag: { ...state.flag, isInitialized: payload } };
        case SET_SELECTED_SECTION_ID:
            return { ...state, flag: { ...state.flag, sectionId: payload } };
        case UPDATE_CURRENT_ACHIEVEMENT_INFO:
            return {
                ...state,
                currentAchievementInfo: { ...state.currentAchievementInfo, ...payload }
            };
        default:
            return state;
    }
}

export function initAchievement(data) {
    return {
        type: INIT_ACHIEVEMENT,
        payload: data
    };
}

export function setAchievementInitialized(data) {
    return {
        type: SET_ACHIEVEMENT_INITIALIZED,
        payload: data
    };
}

export function updateAchievement(data) {
    return {
        type: UPDATE_ACHIEVEMENT,
        payload: data
    };
}

export function setCurrentAchievementInfo(data) {
    return {
        type: UPDATE_ACHIEVEMENT,
        payload: {
            currentAchievementInfo: data
        }
    };
}

/**
 * 取得成就中心列表 ( api & redux )
 * @return  {promise}
 */
export function getAchievementList({ loginMethod, withLevels } = {}) {
    return async (dispatch, getState) => {
        const {
            sponsor: { id: sponsorId }
        } = getState();

        let hasNextPage = true;
        let achievementList = [];
        let page = 1;
        let perPage = 50;

        while (hasNextPage) {
            const nextAchievementList = await getAchievementListApi({
                sponsorId,
                loginMethod,
                page,
                perPage,
                withLevels
            });

            if (nextAchievementList.length < perPage) {
                hasNextPage = false;
            }
            page++;

            achievementList = [...achievementList, ...nextAchievementList];
        }

        dispatch({
            type: UPDATE_ACHIEVEMENT,
            payload: { achievementList }
        });

        return achievementList;
    };
}

/**
 * 取得成就中心列表 api + 更新當前成就中心資訊
 * @return  {promise}
 */
export function getAchievementListAndUpdateCurrentAchievementInfo({ achievementUUID }) {
    return async (dispatch, getState) => {
        const {
            sponsor: { id: sponsorId }
        } = getState();

        let hasNextPage = true;
        let achievementList = [];
        let page = 1;
        let perPage = 50;

        while (hasNextPage) {
            const nextAchievementList = await getAchievementListApi({
                sponsorId,
                page,
                perPage
            });

            if (nextAchievementList.length < perPage) {
                hasNextPage = false;
            }
            page++;

            achievementList = [...achievementList, ...nextAchievementList];
        }

        const { achievement } = await getAchievementSettingApi({
            sponsorId,
            achievementUUID
        });

        achievement.settings = Object.keys(achievement.settings).reduce((acc, key) => {
            acc[key] = objFirstLevelJSONParse(achievement.settings[key]);
            return acc;
        }, {});

        dispatch({
            type: UPDATE_ACHIEVEMENT,
            payload: {
                achievementList,
                currentAchievementInfo: achievement
            }
        });

        return achievementList;
    };
}

/**
 * 取得成就中心等級列表 ( api & redux )
 * @return  {promise}
 */
export function getAchievementLevelList(achievementUUID) {
    return async (dispatch, getState) => {
        const {
            sponsor: { id: sponsorId },
            achievement: { currentAchievementInfo = {} }
        } = getState();

        const achievementLevelList = await getAchievementLevelListApi({
            sponsorId,
            achievementUUID: achievementUUID || currentAchievementInfo.uuid
        });

        dispatch({
            type: UPDATE_ACHIEVEMENT,
            payload: { achievementLevelList }
        });

        return achievementLevelList;
    };
}

/**
 * 取得指定成就中心資料 ( api & redux )
 * @return  {promise}
 */
export function getAchievementSetting(achievementUUID) {
    return async (dispatch, getState) => {
        const {
            sponsor: { id: sponsorId }
        } = getState();

        const { achievement } = await getAchievementSettingApi({
            sponsorId,
            achievementUUID
        });

        achievement.settings = Object.keys(achievement.settings).reduce((acc, key) => {
            acc[key] = objFirstLevelJSONParse(achievement.settings[key]);
            return acc;
        }, {});

        dispatch({
            type: UPDATE_ACHIEVEMENT,
            payload: { currentAchievementInfo: achievement }
        });

        return achievement;
    };
}

export function setSelectedSectionId(sectionId) {
    return {
        type: SET_SELECTED_SECTION_ID,
        payload: sectionId
    };
}

export function updateAchievementSetting(foundationCode, data) {
    return async (dispatch, getState) => {
        const { currentAchievementInfo } = getState().achievement;

        dispatch({
            type: UPDATE_CURRENT_ACHIEVEMENT_INFO,
            payload: {
                ...currentAchievementInfo,
                settings: {
                    ...currentAchievementInfo.settings,
                    [foundationCode]: {
                        ...currentAchievementInfo.settings[foundationCode],
                        ...data
                    }
                }
            }
        });
    };
}
